/**
 * Нормализация GeoJSON для тактической доски: только Point / LineString / Polygon.
 * Multi* разбиваются на простые Feature; прочее пропускается.
 */

export type NormalizeResult = {
  features: GeoJSON.Feature[];
  skipped: number;
};

function asFeature(
  geometry: GeoJSON.Geometry,
  properties: GeoJSON.GeoJsonProperties,
): GeoJSON.Feature {
  return {
    type: "Feature",
    geometry,
    properties: properties ?? {},
  };
}

function explodeGeometry(
  geometry: GeoJSON.Geometry,
  properties: GeoJSON.GeoJsonProperties,
  out: GeoJSON.Feature[],
): number {
  let skipped = 0;
  switch (geometry.type) {
    case "Point":
    case "LineString":
    case "Polygon":
      out.push(asFeature(geometry, properties));
      break;
    case "MultiPoint":
      for (const coordinates of geometry.coordinates) {
        out.push(asFeature({ type: "Point", coordinates }, properties));
      }
      break;
    case "MultiLineString":
      for (const coordinates of geometry.coordinates) {
        out.push(asFeature({ type: "LineString", coordinates }, properties));
      }
      break;
    case "MultiPolygon":
      for (const coordinates of geometry.coordinates) {
        out.push(asFeature({ type: "Polygon", coordinates }, properties));
      }
      break;
    case "GeometryCollection":
      for (const g of geometry.geometries) {
        skipped += explodeGeometry(g, properties, out);
      }
      break;
    default:
      skipped += 1;
  }
  return skipped;
}

/** Feature / FeatureCollection / Geometry → список простых Feature. */
export function normalizeToSimpleFeatures(input: unknown): NormalizeResult {
  const features: GeoJSON.Feature[] = [];
  let skipped = 0;

  if (!input || typeof input !== "object") {
    return { features, skipped: 1 };
  }

  const obj = input as GeoJSON.GeoJSON;

  if (obj.type === "FeatureCollection" && Array.isArray(obj.features)) {
    for (const f of obj.features) {
      if (!f || f.type !== "Feature" || !f.geometry) {
        skipped += 1;
        continue;
      }
      skipped += explodeGeometry(f.geometry, f.properties ?? {}, features);
    }
    return { features, skipped };
  }

  if (obj.type === "Feature") {
    const f = obj as GeoJSON.Feature;
    if (!f.geometry) return { features, skipped: 1 };
    skipped += explodeGeometry(f.geometry, f.properties ?? {}, features);
    return { features, skipped };
  }

  if (
    obj.type === "Point" ||
    obj.type === "LineString" ||
    obj.type === "Polygon" ||
    obj.type === "MultiPoint" ||
    obj.type === "MultiLineString" ||
    obj.type === "MultiPolygon" ||
    obj.type === "GeometryCollection"
  ) {
    skipped += explodeGeometry(obj as GeoJSON.Geometry, {}, features);
    return { features, skipped };
  }

  return { features, skipped: 1 };
}

/** Подготовка фич доски к внешнему GIS-экспорту (без внутренних id-шумов). */
export function featuresForGisExport(
  features: GeoJSON.Feature[],
): GeoJSON.FeatureCollection {
  const { features: simple } = normalizeToSimpleFeatures({
    type: "FeatureCollection",
    features,
  });
  return {
    type: "FeatureCollection",
    features: simple.map((f) => {
      const p = (f.properties ?? {}) as Record<string, unknown>;
      const name =
        (typeof p.label === "string" && p.label) ||
        (typeof p.name === "string" && p.name) ||
        undefined;
      const props: Record<string, unknown> = {};
      if (name) props.name = name;
      if (typeof p.kind === "string") props.kind = p.kind;
      if (typeof p.label === "string") props.label = p.label;
      if (typeof p.strokeColor === "string") props.strokeColor = p.strokeColor;
      if (typeof p.fillColor === "string") props.fillColor = p.fillColor;
      if (typeof p.strokeWidth === "number") props.strokeWidth = p.strokeWidth;
      if (typeof p.fillOpacity === "number") props.fillOpacity = p.fillOpacity;
      if (typeof p.sizePx === "number") props.sizePx = p.sizePx;
      if (typeof p.rotationDeg === "number") props.rotationDeg = p.rotationDeg;
      if (typeof p.symbolKlsId === "number") props.symbolKlsId = p.symbolKlsId;
      return { type: "Feature", geometry: f.geometry, properties: props };
    }),
  };
}
